import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileService, networkService, organizationService } from '../services/api';

import userDataExportService from '../services/userDataExport';
import '../styles/Profile.css';

const Profile = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [networksList, setNetworksList] = useState([]);

  // Enrichir les titres avec le nom de l'organisation après chargement du profil
  useEffect(() => {
    const enrichTitlesWithOrganization = async () => {
      if (profileData && profileData.titles && profileData.titles.length > 0) {
        const updatedTitles = await Promise.all(
          profileData.titles.map(async (title) => {
            if (typeof title.organization === 'string') {
              try {
                const orgDetails = await organizationService.getOrganizationById(title.organization);
                return {
                  ...title,
                  organization: orgDetails?.data || { _id: title.organization, name: '' }
                };
              } catch (e) {
                return { ...title, organization: { _id: title.organization, name: '' } };
              }
            }
            return title;
          })
        );
        setProfileData((prev) => ({ ...prev, titles: updatedTitles }));
      }
    };
    enrichTitlesWithOrganization();
  }, [profileData && profileData.titles]);
  
  // Charger la liste des réseaux disponibles
  useEffect(() => {
    const fetchNetworks = async () => {
      try {
        const networks = await networkService.getNetworks();
        setNetworksList(networks);
      } catch (err) {
        console.error('Erreur lors du chargement des réseaux:', err);
      }
    };
    
    fetchNetworks();
  }, []);
  
  // Fonction pour exporter les données de l'utilisateur (conformité Loi 25)
  const handleExportUserData = async () => {
    try {
      setExportLoading(true);
      setExportError(null);
      setExportSuccess(false);
      
      // Déterminer l'ID de l'utilisateur
      const userId = currentUser?.id;
      
      if (!userId) {
        setExportError('Vous devez être connecté pour exporter vos données');
        return;
      }
      
      await userDataExportService.exportUserData(userId);
      setExportSuccess(true);
      
      // Réinitialiser le message de succès après 5 secondes
      setTimeout(() => {
        setExportSuccess(false);
      }, 5000);
      
    } catch (err) {
      console.error('Erreur lors de l\'exportation des données:', err);
      setExportError('Une erreur est survenue lors de l\'exportation des données. Veuillez réessayer plus tard.');
    } finally {
      setExportLoading(false);
    }
  };

  // Fonction pour ouvrir la boîte de dialogue de confirmation de suppression
  const openDeleteConfirmation = () => {
    setShowDeleteConfirmation(true);
  };

  // Fonction pour fermer la boîte de dialogue de confirmation de suppression
  const closeDeleteConfirmation = () => {
    setShowDeleteConfirmation(false);
  };

  // Fonction pour supprimer complètement les données de l'utilisateur (conformité Loi 25)
  const handleDeleteUserCompleteData = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      
      // Déterminer l'ID de l'utilisateur
      const userId = currentUser?.id;
      
      if (!userId) {
        setDeleteError('Vous devez être connecté pour supprimer vos données');
        return;
      }
      
      // Appel au service pour supprimer complètement les données
      await profileService.deleteUserCompleteData(userId);
      
      // Rediriger vers la page d'accueil après la suppression
      navigate('/', { 
        state: { 
          message: 'Toutes vos données ont été supprimées conformément à la Loi 25 du Québec. Votre session a été terminée.' 
        } 
      });
      
    } catch (err) {
      console.error('Erreur lors de la suppression complète des données:', err);
      setDeleteError('Une erreur est survenue lors de la suppression des données. Veuillez réessayer plus tard.');
      setDeleteLoading(false);
      closeDeleteConfirmation();
    }
  };

  useEffect(() => {
    // Vérifier si c'est le profil de l'utilisateur connecté
    const checkIfOwnProfile = () => {
      if (currentUser && (!id || id === currentUser.id || id === currentUser._id)) {
        console.log('C\'est le profil de l\'utilisateur connecté');
        setIsOwnProfile(true);
        return true;
      } else {
        console.log('Ce n\'est PAS le profil de l\'utilisateur connecté');
        console.log('currentUser:', currentUser);
        console.log('id:', id);
        setIsOwnProfile(false);
        return false;
      }
    };
    
    // Charger les données du profil depuis la base de données
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const isOwn = checkIfOwnProfile();
        
        // Déterminer l'ID à utiliser pour la recherche
        const targetId = id || (currentUser ? (currentUser.id || currentUser._id) : null);
        
        if (!targetId) {
          // Si aucun ID n'est spécifié et que l'utilisateur n'est pas connecté, rediriger vers la page de connexion
          navigate('/login');
          return;
        }
        
        console.log('Chargement du profil avec ID:', targetId);
        
        let data = null;
        
        // D'abord essayer de récupérer par ID de profil
        try {
          console.log('Tentative de récupération par ID de profil...');
          data = await profileService.getProfileById(targetId);
          
          // Si pas de données, essayer avec l'ID utilisateur
          if (!data) {
            console.log('Aucun profil trouvé avec getProfileById, tentative avec getProfileByUserId...');
            data = await profileService.getProfileByUserId(targetId);
          }
        } catch (err) {
          console.error('Erreur lors de la récupération du profil:', err);
          // Continuer pour essayer l'autre méthode
        }
        
        // Si toujours pas de données et que c'est le profil de l'utilisateur connecté
        if (!data && isOwn) {
          // Rediriger vers la page de paramètres pour créer un profil
          navigate('/profile/settings', { 
            state: { message: 'Veuillez compléter votre profil pour continuer.' } 
          });
          return;
        }
        
        // Si toujours pas de données, c'est qu'aucun profil n'existe
        if (!data) {
          throw new Error('Aucun profil trouvé pour cet utilisateur.');
        }
        
        console.log('Données du profil reçues:', data);
        
        // Extraire l'email de l'objet user si disponible
        if (data.user && data.user.email) {
          console.log('Email trouvé dans l\'objet user:', data.user.email);
          data.email = data.user.email;
        } else if (data.email) {
          console.log('Email trouvé directement dans les données du profil:', data.email);
        } else {
          console.log('Aucun email trouvé pour ce profil');
        }
        
        setProfileData(data);
      } catch (err) {
        console.error('Erreur lors du chargement du profil:', err);
        
        // Si c'est le profil de l'utilisateur connecté mais qu'il n'existe pas encore
        if (isOwnProfile) {
          navigate('/profile/settings', { 
            state: { message: 'Veuillez compléter votre profil pour continuer.' } 
          });
          return;
        }
        
        // Pour les autres cas, afficher un message d'erreur approprié
        setError(err.message || 'Impossible de charger les données du profil. Ce profil n\'existe pas ou n\'est pas accessible.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfileData();
  }, [currentUser, id, navigate, isOwnProfile]);
  
  // Afficher un indicateur de chargement pendant le chargement des données
  if (loading) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="loading-indicator">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p>Chargement du profil...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Afficher un message d'erreur si le chargement a échoué
  if (error) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Profil non trouvé</h4>
            <p>{error}</p>
            <hr />
            <p className="mb-0">
              {currentUser && (currentUser.id === id || currentUser._id === id) ? (
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/profile/settings')}
                >
                  Créer mon profil
                </button>
              ) : (
                <button 
                  className="btn btn-secondary"
                  onClick={() => navigate(-1)}
                >
                  Retour
                </button>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Afficher un message si le profil n'existe pas
  if (!profileData) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="error-message">Profil introuvable.</div>
        </div>
      </div>
    );
  }
  
  // Afficher des informations de débogage dans la console
  console.log('Rendu du composant Profile');
  console.log('isOwnProfile:', isOwnProfile);
  console.log('currentUser:', currentUser);
  console.log('profileData:', profileData);
  
  // Vérifier si l'utilisateur est sur son propre profil ou en mode admin
  const isAdminView = window.location.pathname.startsWith('/admin');
  const showLaw25Actions = isOwnProfile || isAdminView;
  
  return (
    <div className="profile-page">
      <div className="container">
        {/* Boutons d'action - visibles uniquement pour le propriétaire du profil ou en mode admin */}
        {currentUser && (isOwnProfile || isAdminView) && (
          <div className="profile-actions">
            <Link to="/profile/settings" className="btn btn-primary">
              <i className="fas fa-edit"></i> {isAdminView ? 'Modifier le profil' : 'Modifier mon profil'}
            </Link>
            
            {/* Bouton d'exportation des données (Loi 25) */}
            {showLaw25Actions && (
              <button 
                onClick={handleExportUserData} 
                className="btn btn-info ml-2"
                disabled={exportLoading}
                style={{ marginLeft: '10px', fontWeight: 'bold' }}
              >
                <i className="fas fa-download"></i> {exportLoading ? 'Exportation en cours...' : `Exporter ${isAdminView ? 'les' : 'mes'} données (Loi 25)`}
              </button>
            )}
            
            {/* Bouton de suppression des données (Loi 25) - uniquement sur son propre profil */}
            {isOwnProfile && (
              <button 
                onClick={openDeleteConfirmation} 
                className="btn btn-danger ml-2"
                disabled={deleteLoading}
                style={{ marginLeft: '10px', fontWeight: 'bold' }}
              >
                <i className="fas fa-trash-alt"></i> {deleteLoading ? 'Suppression en cours...' : 'Supprimer mes données (Loi 25)'}
              </button>
            )}
            {exportError && (
              <div className="alert alert-danger mt-2">
                {exportError}
              </div>
            )}
            {exportSuccess && (
              <div className="alert alert-success mt-2">
                Vos données ont été exportées avec succès.
              </div>
            )}
            <div className="privacy-notice mt-2">
              <small>
                Conformément à la Loi 25 du Québec sur la protection des renseignements personnels, 
                vous pouvez télécharger l'ensemble des données que nous conservons sur vous.
              </small>
            </div>
          </div>
        )}
        {/* Boîte de dialogue de confirmation pour la suppression complète des données */}
        {showDeleteConfirmation && (
          <div className="modal-overlay" style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0, 0, 0, 0.5)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            zIndex: 1000 
          }}>
            <div className="modal-content" style={{ 
              backgroundColor: 'white', 
              padding: '20px', 
              borderRadius: '5px', 
              maxWidth: '500px', 
              width: '90%' 
            }}>
              <h2 style={{ color: '#dc3545', marginBottom: '15px' }}>Confirmer la suppression complète</h2>
              <p style={{ marginBottom: '15px' }}>
                <strong>ATTENTION:</strong> Vous êtes sur le point de supprimer <strong>DÉFINITIVEMENT</strong> toutes vos données, 
                y compris tous les fichiers associés à votre profil.
              </p>
              <p style={{ marginBottom: '15px' }}>
                Cette action est irréversible et est effectuée conformément à la Loi 25 du Québec sur la protection 
                des renseignements personnels.
              </p>
              {deleteError && (
                <div className="alert alert-danger" style={{ marginBottom: '15px' }}>
                  {deleteError}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={closeDeleteConfirmation}
                  disabled={deleteLoading}
                >
                  Annuler
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={handleDeleteUserCompleteData}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Suppression en cours...' : 'Supprimer définitivement mes données'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="profile-header">
          <div className="profile-photo">
            <img src={profileData.photo && profileData.photo.trim() ? profileData.photo : '/images/placeholder-user.jpg'} alt={`${profileData.firstName} ${profileData.lastName}`} />
          </div>
          <div className="profile-header-info">
            <div className="profile-name-container">
              <h1 className="profile-name">{profileData.firstName} {profileData.lastName}</h1>
              {profileData.pronouns && <p className="profile-pronouns">({profileData.pronouns})</p>}
            </div>
            
            <div className="profile-titles">
              {profileData.titles && profileData.titles.map((title, index) => (
                <div key={index} className="profile-title">
                  <h3>{title.title} - {title.organization?.name}</h3>
                  {title.department && <p>{title.department}</p>}
                </div>
              ))}
            </div>
            
            <div className="profile-contact">
              <div className="contact-item">
                <i className="fas fa-envelope"></i>
                <a href={`mailto:${profileData.user?.email || profileData.email || ''}`}>{profileData.user?.email || profileData.email || ''}</a>
              </div>
              {profileData.phone && (
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <span>{profileData.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="profile-content">
          <div className="profile-main">
            {profileData.datasets && profileData.datasets.length > 0 && (
              <div className="profile-section">
                <h2 className="section-title">Jeux de données</h2>
                <div className="datasets-list">
                  {profileData.datasets.map((dataset, index) => (
                    <div key={index} className="dataset-item">
                      <h3 className="dataset-name">{dataset.name}</h3>
                      {dataset.doi && (
                        <span className="item-doi" style={{ display: 'block', fontSize: '0.95em', color: '#007199', marginTop: '2px' }}>
                          DOI : <a href={`https://doi.org/${dataset.doi}`} target="_blank" rel="noopener noreferrer">{dataset.doi}</a>
                        </span>
                      )}
                      {dataset.url && (
                        <a href={dataset.url} target="_blank" rel="noopener noreferrer" className="dataset-link">
                          <i className="fas fa-database"></i> Accéder au jeu de données
                        </a>
                      )}
                      {dataset.fundedByRQM && (
                        <span className="rqm-funded-badge" style={{ display: 'inline-block', background: '#e6f7fa', color: '#007199', borderRadius: '4px', padding: '2px 8px', fontSize: '0.85em', marginLeft: '8px' }}>
                          Campagne RQM
                        </span>
                      )}
                      {dataset.description && (
                        <p className="dataset-description">{dataset.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {profileData.languages && profileData.languages.length > 0 && (
              <div className="profile-section">
                <h2 className="section-title">Langues</h2>
                <div className="languages-list">
                  {profileData.languages.map((language, index) => (
                    <span key={index} className="language-item">{language}</span>
                  ))}
                </div>
              </div>
            )}
            
            {profileData.expertise && profileData.expertise.length > 0 && (
              <div className="profile-section">
                <h2 className="section-title">Expertises</h2>
                <div className="expertise-tags">
                  {profileData.expertise.map((item, index) => (
                    <span key={index} className="badge badge-primary">{item}</span>
                  ))}
                </div>
              </div>
            )}
            
            {profileData.projects && profileData.projects.length > 0 && (
              <div className="profile-section">
                <h2 className="section-title">Projets de recherche</h2>
                
                {/* Filtrer et afficher d'abord les projets financés par le RQM */}
                {profileData.projects.some(project => project.fundedByRQM) && (
                  <div className="rqm-projects">
                    <h3 className="subsection-title">
                      <i className="fas fa-award"></i> Projets financés par le RQM
                    </h3>
                    <div className="projects-list">
                      {profileData.projects
                        .filter(project => project.fundedByRQM)
                        .map((project, index) => (
                          <div key={`rqm-${index}`} className="project-item project-item-rqm">
                            <div className="project-header">
                              <h3 className="project-title">{project.name}</h3>
                              <span className="badge badge-rqm">
                                <i className="fas fa-award"></i> RQM
                              </span>
                            </div>
                            {project.description && <p className="project-description">{project.description}</p>}
                            {project.url && (
                              <a href={project.url} target="_blank" rel="noopener noreferrer" className="project-link">
                                <i className="fas fa-external-link-alt"></i> Site du projet
                              </a>
                            )}
                            {project.startDate && (
                              <div className="project-dates">
                                <span>Début: {new Date(project.startDate).toLocaleDateString()}</span>
                                {project.endDate && (
                                  <span> - Fin: {new Date(project.endDate).toLocaleDateString()}</span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                
                {/* Afficher les autres projets */}
                {profileData.projects.some(project => !project.fundedByRQM) && (
                  <div className="other-projects">
                    {profileData.projects.some(project => project.fundedByRQM) && (
                      <h3 className="subsection-title">Autres projets</h3>
                    )}
                    <div className="projects-list">
                      {profileData.projects
                        .filter(project => !project.fundedByRQM)
                        .map((project, index) => (
                          <div key={`other-${index}`} className="project-item">
                            <h3 className="project-title">{project.name}</h3>
                            {project.description && <p className="project-description">{project.description}</p>}
                            {project.url && (
                              <a href={project.url} target="_blank" rel="noopener noreferrer" className="project-link">
                                <i className="fas fa-external-link-alt"></i> Site du projet
                              </a>
                            )}
                            {project.startDate && (
                              <div className="project-dates">
                                <span>Début: {new Date(project.startDate).toLocaleDateString()}</span>
                                {project.endDate && (
                                  <span> - Fin: {new Date(project.endDate).toLocaleDateString()}</span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {profileData.infrastructure && profileData.infrastructure.length > 0 && (
              <div className="profile-section">
                <h2 className="section-title">Infrastructures</h2>
                <ul className="infrastructure-list">
                  {profileData.infrastructure.map((item, index) => (
                    <li key={index}>
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <div className="profile-sidebar">
            {profileData.networks && profileData.networks.length > 0 && (
              <div className="profile-section">
                <h2 className="section-title">Réseaux et regroupements</h2>
                <ul className="networks-list">
                  {profileData.networks.map((networkId, index) => {
                    // Trouver le réseau correspondant dans la liste des réseaux
                    const networkDetails = networksList.find(n => n._id === networkId);
                    // Afficher le nom du réseau s'il est trouvé, sinon afficher l'ID
                    return (
                      <li key={index} className="network-item">
                        {networkDetails ? networkDetails.name : networkId}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          
            {profileData.websites && profileData.websites.length > 0 && (
              <div className="profile-section">
                <h2 className="section-title">Sites web</h2>
                <ul className="websites-list">
                  {profileData.websites.map((site, index) => (
                    <li key={index}>
                      <a href={site.url} target="_blank" rel="noopener noreferrer">
                        {site.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {profileData.socialMedia && profileData.socialMedia.length > 0 && (
              <div className="profile-section">
                <h2 className="section-title">Réseaux sociaux</h2>
                <div className="social-links">
                  {profileData.socialMedia.map((social, index) => {
                    // Gérer les cas spéciaux pour certaines plateformes
                    let iconClass = social.platform.toLowerCase();
                    let displayName = social.platform;
                    
                    // Cas spéciaux pour certaines plateformes
                    if (iconClass === 'x') {
                      iconClass = 'twitter';
                      displayName = 'X (Twitter)';
                    } else if (iconClass === 'researchgate') {
                      iconClass = 'researchgate';
                    } else if (iconClass === 'autre') {
                      iconClass = 'link';
                    }
                    
                    return (
                      <a 
                        key={index} 
                        href={social.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`social-link social-link-${iconClass}`}
                        title={displayName}
                      >
                        <i className={`fab fa-${iconClass}`}></i>
                        <span>{displayName}</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
            
            {profileData.cv && profileData.cv.url && (
              <div className="profile-section">
                <h2 className="section-title">CV</h2>
                <div className="cv-link">
                  {profileData.cv.fileType === 'pdf' ? (
                    <button onClick={() => profileService.downloadCV(profileData.cv.url)} className="btn btn-outline-primary">
                      <i className="fas fa-file-pdf"></i> Télécharger le CV
                    </button>
                  ) : (
                    <a href={profileData.cv.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">
                      <i className="fas fa-external-link-alt"></i> Voir le CV en ligne
                    </a>
                  )}
                  {profileData.cv.lastUpdated && (
                    <div className="cv-update-date">
                      <small>Mis à jour le {new Date(profileData.cv.lastUpdated).toLocaleDateString()}</small>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {profileData.documents && Array.isArray(profileData.documents) && profileData.documents.length > 0 && (
              <div className="profile-section">
                <h2 className="section-title">Documents</h2>
                <div className="documents-list">
                  {profileData.documents
                    // Filtrer les documents temporaires qui ne devraient pas être affichés dans la page de profil
                    .filter(doc => doc && !doc._id?.toString().startsWith('temp_'))
                    .map((document, index) => {
                      // Vérifier que le document existe et a les propriétés nécessaires
                      if (!document || !document.name) {
                        console.error('Document invalide à l\'index', index, document);
                        return null;
                      }
                      
                      return (
                        <div key={index} className="document-item">
                          {document.url ? (
                            document.fileType === 'pdf' ? (
                              <h3 className="document-name">
                                <a href="#" onClick={e => { e.preventDefault(); profileService.downloadDocument(document.url); }} style={{ textDecoration: 'underline', color: '#007199' }}>
                                  {document.name || 'Document sans nom'} <i className="fas fa-file-pdf"></i>
                                </a>
                              </h3>
                            ) : (
                              <h3 className="document-name">
                                <a href={document.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#007199' }}>
                                  {document.name || 'Document sans nom'}&nbsp;&nbsp;<i className="fas fa-external-link-alt"></i>
                                </a>
                              </h3>
                            )
                          ) : (
                            <h3 className="document-name">{document.name || 'Document sans nom'}</h3>
                          )}
                          {document.description && (
                            <p className="document-description">{document.description}</p>
                          )}
                          {document.lastUpdated && (
                            <div className="document-update-date">
                              <small>Mis à jour le {new Date(document.lastUpdated).toLocaleDateString()}</small>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
